import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Container, Grid, Paper, Typography, Card, CardContent, 
  CardMedia, CardActions, Divider, useTheme, useMediaQuery, Avatar,
  Fade, Zoom, Chip, Stack, IconButton, Tooltip, Collapse, Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import HomeIcon from '@mui/icons-material/Home';
import { supabase } from '../supabase/config';
import { useSupabaseAuth } from '../hooks/useSupabaseAuth';
import Footer from './Footer';

// Background elements component - adds visual interest
const BackgroundElements = () => {
  return (
    <>
      {/* Top right decorative element */}
      <Box
        sx={{
          position: 'absolute',
          top: { xs: -100, md: -150 },
          right: { xs: -100, md: -150 },
          width: { xs: 200, md: 300 },
          height: { xs: 200, md: 300 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.2) 0%, rgba(156, 39, 176, 0.05) 60%)',
          zIndex: 0
        }}
      />
      
      {/* Bottom left decorative element */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: -80, md: -120 },
          left: { xs: -80, md: -120 },
          width: { xs: 180, md: 240 },
          height: { xs: 180, md: 240 },
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255, 152, 0, 0.2) 0%, rgba(255, 152, 0, 0.05) 60%)',
          zIndex: 0
        }}
      />
      
      {/* Small accent circles */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            top: `${10 + (i * 15)}%`,
            left: `${70 + (i % 3) * 10}%`,
            width: { xs: 8, md: 12 },
            height: { xs: 8, md: 12 },
            borderRadius: '50%',
            bgcolor: i % 2 === 0 ? 'primary.main' : 'secondary.main',
            opacity: 0.15,
            zIndex: 0
          }}
        />
      ))}
    </>
  );
};

// Feature card component - reusable for each feature
const FeatureCard = ({ icon, title, description, gradient, color, path, delay }) => {
  const navigate = useNavigate();
  
  return (
    <Zoom in={true} style={{ transitionDelay: delay || '0ms' }}>
      <Card
        elevation={0}
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.12)'
          },
          background: gradient,
          border: '1px solid rgba(0,0,0,0.05)'
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 4, textAlign: 'center' }}>
          <Avatar
            sx={{
              width: 70,
              height: 70,
              bgcolor: `${color}15`,
              color: color,
              mb: 2,
              mx: 'auto'
            }}
          >
            {icon}
          </Avatar>
          <Typography gutterBottom variant="h5" component="h3" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
        </CardContent>
        <CardActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
          <Button 
            size="large" 
            endIcon={<ArrowForwardIcon />}
            onClick={() => navigate(path)}
            sx={{ 
              color: color,
              '&:hover': {
                backgroundColor: `${color}15`
              }
            }}
          >
            Try It Now
          </Button>
        </CardActions>
      </Card>
    </Zoom>
  );
};

// Testimonial card component
const TestimonialCard = ({ quote, author, image, delay }) => (
  <Fade in={true} style={{ transitionDelay: delay || '0ms' }}>
    <Card
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.05)',
        overflow: 'visible',
        position: 'relative',
      }}
    >
      <CardContent sx={{ flexGrow: 1, p: 4, pt: 6 }}>
        <Avatar
          src={image}
          sx={{
            width: 70,
            height: 70,
            border: '4px solid white',
            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
            position: 'absolute',
            top: -35,
            left: 30
          }}
        />
        <Box sx={{ display: 'flex', mb: 3, color: 'secondary.main' }}>
          <FavoriteIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <FavoriteIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <FavoriteIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <FavoriteIcon sx={{ fontSize: 18, mr: 0.5 }} />
          <FavoriteIcon sx={{ fontSize: 18 }} />
        </Box>
        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mb: 3 }}>
          "{quote}"
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" fontWeight="bold">
          {author}
        </Typography>
      </CardContent>
    </Card>
  </Fade>
);

// FAQ Item component
const FAQItem = ({ question, answer }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <Card 
      elevation={0} 
      sx={{ 
        mb: 2, 
        borderRadius: 2,
        border: '1px solid rgba(0,0,0,0.08)'
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer'
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            {question}
          </Typography>
          <IconButton size="small">
            <ExpandMoreIcon sx={{ 
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.3s ease'
            }} />
          </IconButton>
        </Box>
        <Collapse in={expanded}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'primary.main' }}
          >
            {answer}
          </Typography>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const [scrolled, setScrolled] = useState(false);
  
  // Detect scroll position to animate elements
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Psychology-informed color palette
  const colors = {
    primary: theme.palette.primary.main, // Purple - creativity, wisdom
    secondary: theme.palette.secondary.main, // Orange - energy, appetite
    accent: '#4CAF50', // Green - health, freshness
    background: '#f8f5fd', // Light purple background - calming
    mealPlannerGradient: 'linear-gradient(135deg, rgba(156, 39, 176, 0.15) 0%, rgba(156, 39, 176, 0.05) 100%)',
    recipeGeneratorGradient: 'linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(76, 175, 80, 0.05) 100%)',
    calendarGradient: 'linear-gradient(135deg, rgba(255, 152, 0, 0.15) 0%, rgba(255, 152, 0, 0.05) 100%)',
  };
  
  const features = [
    {
      title: 'AI Recipe Generator',
      description: 'Create personalized recipes based on ingredients you have available',
      icon: <RestaurantIcon sx={{ fontSize: 40 }} />,
      color: colors.accent,
      path: '/recipe-generator',
      gradient: colors.recipeGeneratorGradient,
      delay: '100ms'
    },
    {
      title: 'Meal Planner',
      description: 'Generate complete meal plans tailored to your dietary needs',
      icon: <EventNoteIcon sx={{ fontSize: 40 }} />,
      color: colors.primary,
      path: '/meal-planner',
      gradient: colors.mealPlannerGradient,
      delay: '200ms'
    },
    {
      title: 'Meal Calendar',
      description: 'Schedule and organize your meals throughout the week',
      icon: <CalendarMonthIcon sx={{ fontSize: 40 }} />,
      color: colors.secondary,
      path: '/meal-calendar',
      gradient: colors.calendarGradient,
      delay: '300ms'
    }
  ];

  const testimonials = [
    {
      quote: "Meal Maestro transformed my meal planning routine! I save hours each week and discovered so many new recipes my family loves.",
      author: "Sarah J.",
      image: "https://randomuser.me/api/portraits/women/44.jpg",
      delay: '200ms'
    },
    {
      quote: "As someone with dietary restrictions, finding suitable recipes is usually hard. This makes it so easy and the results are delicious!",
      author: "Michael R.",
      image: "https://randomuser.me/api/portraits/men/32.jpg",
      delay: '400ms'
    },
    {
      quote: "I used to waste so much food, but the recipe generator helps me use what I already have in my fridge. Game changer!",
      author: "Priya K.",
      image: "https://randomuser.me/api/portraits/women/63.jpg",
      delay: '600ms'
    },
  ];
  
  const faqItems = [
    {
      question: "How does the AI recipe generator work?",
      answer: "Our AI recipe generator uses advanced machine learning to create personalized recipes based on your available ingredients, dietary preferences, and nutritional goals. Simply enter what you have in your kitchen, set your preferences, and let our AI do the rest!"
    },
    {
      question: "Is there a limit to how many meal plans I can create?",
      answer: "No, there's no limit! Free users can create as many meal plans as they need. Premium users get additional features like advanced nutritional analysis and unlimited recipe saves."
    },
    {
      question: "Can I customize my dietary restrictions?",
      answer: "Absolutely! You can specify dietary restrictions like vegetarian, vegan, gluten-free, dairy-free, keto, and many more. Our AI will create recipes and meal plans that respect these restrictions while still being delicious."
    },
    {
      question: "How do I save my favorite recipes?",
      answer: "Once you've generated a recipe you like, simply click the bookmark icon to save it to your profile. You'll need to be logged in to save recipes, and you can access all your saved recipes from your profile page."
    },
    {
      question: "Can I export my meal plans to PDF?",
      answer: "Yes! Both recipes and meal plans can be exported to beautifully formatted PDF documents that include all the information you need for grocery shopping and cooking."
    }
  ];
  
  const nutritionStats = [
    { label: "Calories saved", value: "20-30%", description: "on average when following a meal plan" },
    { label: "Food waste reduction", value: "40%", description: "by using ingredients you already have" },
    { label: "Time saved", value: "5+ hours", description: "per week on meal planning and decisions" },
    { label: "Diet adherence", value: "85%", description: "higher success rate with personalized plans" }
  ];

  return (
    <Box sx={{ bgcolor: colors.background }}>
      {/* Hero Section */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          backgroundColor: 'white',
          color: '#333',
          mb: 4,
          pt: 8,
          pb: { xs: 8, md: 12 },
          overflow: 'hidden'
        }}
      >
        <BackgroundElements />
        
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6} sx={{ position: 'relative', zIndex: 1 }}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    component="h1"
                    variant="h2"
                    color="inherit"
                    gutterBottom
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      lineHeight: 1.2,
                      background: `linear-gradient(45deg, ${colors.primary}, ${colors.secondary})`,
                      backgroundClip: 'text',
                      textFillColor: 'transparent',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 2
                    }}
                  >
                    Your AI-Powered<br />Kitchen Assistant
                  </Typography>
                </Box>
              </Fade>
              
              <Fade in={true} timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Typography
                  variant="h5"
                  color="inherit"
                  paragraph
                  sx={{ fontWeight: 400, mb: 4 }}
                >
                  Create recipes, plan meals, simplify your life
                </Typography>
              </Fade>
              
              <Fade in={true} timeout={1000} style={{ transitionDelay: '600ms' }}>
                <Typography variant="body1" paragraph color="text.secondary" sx={{ mb: 4, maxWidth: '90%' }}>
                  <b>Meal Maestro</b> combines AI with nutrition science to transform your 
                  ingredients into delicious meals, create personalized meal plans, 
                  and help you eat healthier with less effort.
                </Typography>
              </Fade>
              
              <Fade in={true} timeout={1000} style={{ transitionDelay: '900ms' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/recipe-generator')}
                    sx={{
                      px: 4,
                      py: 1.8,
                      bgcolor: colors.primary,
                      '&:hover': { bgcolor: theme.palette.primary.dark },
                      borderRadius: 2,
                      fontSize: '1rem'
                    }}
                  >
                    Create Recipe
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/meal-planner')}
                    sx={{
                      px: 4,
                      py: 1.8,
                      color: colors.secondary,
                      borderColor: colors.secondary,
                      '&:hover': { 
                        borderColor: colors.secondary,
                        bgcolor: `${colors.secondary}10`
                      },
                      borderRadius: 2,
                      fontSize: '1rem'
                    }}
                  >
                    Plan Meals
                  </Button>
                </Stack>
              </Fade>
              
              {/* Feature chips */}
              <Fade in={true} timeout={1000} style={{ transitionDelay: '1200ms' }}>
                <Box sx={{ mt: 5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                    label="AI-Powered" 
                    size="small"
                    sx={{ bgcolor: `${colors.primary}15`, fontWeight: 500 }} 
                  />
                  <Chip 
                    icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                    label="Personalized Plans" 
                    size="small"
                    sx={{ bgcolor: `${colors.secondary}15`, fontWeight: 500 }} 
                  />
                  <Chip 
                    icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                    label="Dietary Preferences" 
                    size="small"
                    sx={{ bgcolor: `${colors.accent}15`, fontWeight: 500 }} 
                  />
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} md={6}>
              <Zoom in={true} style={{ transitionDelay: '500ms' }}>
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 300, md: 400 },
                    width: '100%',
                    mt: { xs: 2, md: 0 }
                  }}
                >
                  <Paper
                    elevation={6}
                    sx={{
                      position: 'absolute',
                      width: { xs: '80%', md: '85%' },
                      height: { xs: 240, md: 320 },
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                    }}
                  >
                    <CardMedia
                      component="img"
                      image="https://images.unsplash.com/photo-1543353071-087092ec393a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80"
                      alt="Delicious meal prepared using Meal Maestro"
                      sx={{ height: '100%', objectFit: 'cover' }}
                    />
                  </Paper>
                  
                  {/* Floating elements */}
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      width: { xs: 100, md: 140 },
                      py: 1.5,
                      px: 2,
                      borderRadius: 3,
                      left: { xs: '5%', md: '0%' },
                      top: { xs: '15%', md: '10%' },
                      backgroundColor: 'white',
                      zIndex: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <RestaurantIcon sx={{ color: colors.accent, mb: 1 }} />
                    <Typography variant="subtitle2" align="center" sx={{ fontWeight: 600 }}>AI Recipe</Typography>
                    <Typography variant="caption" align="center" sx={{ color: 'text.secondary' }}>
                      From ingredients to meal
                    </Typography>
                  </Paper>
                  
                  <Paper
                    elevation={4}
                    sx={{
                      position: 'absolute',
                      width: { xs: 100, md: 140 },
                      py: 1.5,
                      px: 2,
                      borderRadius: 3,
                      right: { xs: '5%', md: '0%' },
                      bottom: { xs: '15%', md: '10%' },
                      backgroundColor: 'white',
                      zIndex: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center'
                    }}
                  >
                    <EventNoteIcon sx={{ color: colors.primary, mb: 1 }} />
                    <Typography variant="subtitle2" align="center" sx={{ fontWeight: 600 }}>Meal Plans</Typography>
                    <Typography variant="caption" align="center" sx={{ color: 'text.secondary' }}>
                      Weekly planning made easy
                    </Typography>
                  </Paper>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
          
          {/* Trusted by section */}
          <Fade in={true} timeout={1000} style={{ transitionDelay: '1400ms' }}>
            <Box sx={{ mt: { xs: 4, md: 8 }, textAlign: 'center' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                USED AND LOVED BY THOUSANDS
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                flexWrap: 'wrap',
                gap: { xs: 4, md: 6 },
                opacity: 0.7
              }}>
                {['Healthline', 'Cooking Light', 'Food Network', 'Nutrition Today', 'Wellness Weekly'].map((partner) => (
                  <Typography 
                    key={partner} 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 700, 
                      fontSize: { xs: '1rem', md: '1.25rem' }
                    }}
                  >
                    {partner}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Paper>
      
      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="overline" 
            component="div" 
            sx={{ 
              color: colors.primary, 
              fontWeight: 600, 
              letterSpacing: 1.5,
              mb: 1
            }}
          >
            OUR FEATURES
          </Typography>
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            Everything You Need for Meal Success
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Our AI-powered tools streamline your meal planning process from start to finish,
            helping you eat healthier and save time.
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {features.map((feature) => (
            <Grid item xs={12} md={4} key={feature.title}>
              <FeatureCard {...feature} />
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* How It Works Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              sx={{ 
                color: colors.secondary, 
                fontWeight: 600, 
                letterSpacing: 1.5,
                mb: 1
              }}
            >
              SIMPLIFIED PROCESS
            </Typography>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              How It Works
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
              Get started in just a few simple steps and transform your cooking routine
            </Typography>
          </Box>
          
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              {
                step: 1,
                title: "Enter Your Preferences",
                description: "Tell us about your ingredients, dietary needs, and nutritional goals.",
                icon: <MenuBookIcon sx={{ fontSize: 30, color: colors.primary }} />,
                delay: '100ms'
              },
              {
                step: 2,
                title: "Our AI Creates Your Plan",
                description: "Our advanced AI generates personalized recipes and meal plans based on your inputs.",
                icon: <TipsAndUpdatesIcon sx={{ fontSize: 30, color: colors.primary }} />,
                delay: '300ms'
              },
              {
                step: 3,
                title: "Enjoy & Share",
                description: "Save your favorites, schedule meals, and share with family and friends.",
                icon: <LocalDiningIcon sx={{ fontSize: 30, color: colors.primary }} />,
                delay: '500ms'
              }
            ].map((step) => (
              <Grid item xs={12} md={4} key={step.step}>
                <Zoom in={true} style={{ transitionDelay: step.delay }}>
                  <Card 
                    elevation={0} 
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      position: 'relative',
                      overflow: 'visible',
                      p: 3,
                      border: '1px solid rgba(0,0,0,0.08)'
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: `${colors.primary}15`,
                        color: colors.primary,
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        position: 'absolute',
                        top: -20,
                        left: 20,
                        border: '4px solid white',
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                      }}
                    >
                      {step.step}
                    </Avatar>
                    <Box sx={{ mt: 4, textAlign: 'center', pt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: colors.primary,
                            width: 50,
                            height: 50
                          }}
                        >
                          {step.icon}
                        </Avatar>
                      </Box>
                      <Typography variant="h6" gutterBottom fontWeight="600">
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {step.description}
                      </Typography>
                    </Box>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>
          
          {/* Stats section */}
          <Box 
            sx={{ 
              mt: 8,
              p: 4,
              borderRadius: 4,
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="h5" fontWeight="bold" color="white" gutterBottom>
                  The Numbers Speak For Themselves
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 2 }}>
                  Our users experience real results with our AI-powered meal planning tools
                </Typography>
                <Button
                  variant="contained"
                  color="inherit"
                  size="large"
                  onClick={() => navigate('/signup')}
                  sx={{
                    color: colors.primary,
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    }
                  }}
                >
                  Join Now
                </Button>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  {nutritionStats.map((stat, index) => (
                    <Grid item xs={6} sm={3} key={index}>
                      <Card
                        elevation={0}
                        sx={{
                          p: 2,
                          height: '100%',
                          textAlign: 'center',
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.9)'
                        }}
                      >
                        <Typography variant="h4" fontWeight="bold" color={colors.primary}>
                          {stat.value}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          {stat.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {stat.description}
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
      
      {/* Testimonials */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="overline" 
            component="div" 
            sx={{ 
              color: colors.accent, 
              fontWeight: 600, 
              letterSpacing: 1.5,
              mb: 1
            }}
          >
            TESTIMONIALS
          </Typography>
          <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
            What Our Users Say
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
            Join thousands of satisfied users who have transformed their cooking experience
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {testimonials.map((testimonial, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <TestimonialCard {...testimonial} />
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* FAQ Section */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              sx={{ 
                color: colors.primary, 
                fontWeight: 600, 
                letterSpacing: 1.5,
                mb: 1
              }}
            >
              FREQUENTLY ASKED QUESTIONS
            </Typography>
            <Typography variant="h4" component="h2" gutterBottom fontWeight="bold">
              Common Questions
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              {faqItems.slice(0, Math.ceil(faqItems.length / 2)).map((item, index) => (
                <FAQItem key={index} {...item} />
              ))}
            </Grid>
            <Grid item xs={12} md={6}>
              {faqItems.slice(Math.ceil(faqItems.length / 2)).map((item, index) => (
                <FAQItem key={index + Math.ceil(faqItems.length / 2)} {...item} />
              ))}
            </Grid>
          </Grid>
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Still have questions? We're here to help!
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              component={Link}
              href="mailto:support@mealmaestro.com"
              sx={{ borderRadius: 2 }}
            >
              Contact Support
            </Button>
          </Box>
        </Container>
      </Box>
      
      {/* Call to Action */}
      <Paper
        elevation={0}
        sx={{
          bgcolor: colors.primary,
          color: 'white',
          py: 8,
          mt: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <BackgroundElements />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography variant="h3" gutterBottom fontWeight="bold">
              Start Your Meal Journey Today
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 'normal', mb: 4 }}>
              Join thousands of users who are simplifying their meal planning with AI
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate(user ? '/recipe-generator' : '/signup')}
              sx={{
                px: 6,
                py: 2,
                bgcolor: 'white',
                color: colors.primary,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                borderRadius: 2,
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                fontSize: '1.1rem'
              }}
            >
              {user ? 'Create Your First Recipe' : 'Get Started For Free'}
            </Button>
            
            {!user && (
              <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
                No credit card required. Free plan available.
              </Typography>
            )}
          </Box>
          
          {!isMedium && (
            <Grid container spacing={2} justifyContent="center">
              {['Personalized Recipes', 'Weekly Meal Plans', 'Nutrition Tracking', 'Shopping Lists', 'Calendar Integration'].map((feature) => (
                <Grid item key={feature}>
                  <Chip 
                    label={feature} 
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.15)', 
                      color: 'white',
                      borderRadius: 1,
                      '& .MuiChip-label': {
                        px: 1.5
                      }
                    }} 
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Paper>
      
      {/* Footer with links to Terms, Privacy, and Contact */}
      <Footer />
    </Box>
  );
};

export default Home;
